import { useContext } from "react";
import { AuthContext, AuthContextType } from "../contexts/AuthContext";

/**
 * Auth Hook
 * Provides access to authentication state and operations
 *
 * NOTE: This hook now uses AuthContext to share authentication state across all components.
 * Authentication is checked once when AuthProvider mounts, preventing duplicate checks
 * and ensuring all components see the same auth state.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, login, logout } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <LoginForm onSubmit={login} />;
 *   }
 *
 *   return <div>Welcome {user?.userName}!</div>;
 * }
 * ```
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

// Export the return type for backward compatibility
export type UseAuthReturn = AuthContextType;
